/* ────────────────────────────────────────────────────────────────────────────
   FEATURED — CINEMATIC SPOTLIGHT (arrow-navigated, v18)
   No scroll capture. Page scrolls past normally. Left/right arrows step one
   card at a time, with an eased spring glide between cards. Click-and-drag is
   still supported. Per-card pulse glow at rest, caption fades during motion,
   per-image sampled tint of the backdrop.
   ─────────────────────────────────────────────────── */
'use strict';
(function () {
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return [h * 360, s, l];
  }
  function prominentHue(data) {
    var bestKey = null, bestCount = 0, accum = {};
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 125) continue;
      var r = data[i], g = data[i + 1], b = data[i + 2];
      var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      if (mx > 244 && mn > 232) continue;
      if (mx < 18) continue;
      var hsl = rgbToHsl(r, g, b);
      if (hsl[1] < 0.16) continue;
      var hb = Math.round(hsl[0] / 18) * 18;
      var key = hb + '_' + (Math.round(hsl[1] * 4) / 4);
      var bk = accum[key] || (accum[key] = { c: 0, h: 0, s: 0 });
      bk.c++; bk.h += hsl[0]; bk.s += hsl[1];
      if (bk.c > bestCount) { bestCount = bk.c; bestKey = key; }
    }
    if (bestKey) { var x = accum[bestKey]; return [x.h / x.c, x.s / x.c]; }
    return null;
  }
  function sampleImage(img) {
    try {
      var c = document.createElement('canvas');
      c.width = 28; c.height = 40;
      var ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, 28, 40);
      return prominentHue(ctx.getImageData(0, 0, 28, 40).data);
    } catch (e) { return null; }
  }

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

  function initSection(root) {
    if (root.__fcxInit) return;
    root.__fcxInit = true;

    var panel = root.querySelector('[data-fcx-panel]');
    var stage = root.querySelector('[data-fcx-stage]');
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-fcx-item]'));
    var caps = Array.prototype.slice.call(root.querySelectorAll('[data-fcx-cap]'));
    var tintEl = root.querySelector('[data-fcx-tint]');
    var prevBtn = root.querySelector('[data-fcx-prev]');
    var nextBtn = root.querySelector('[data-fcx-next]');
    var N = items.length;
    if (!N) return;

    var tintStrength = (parseFloat(root.getAttribute('data-tint')) || 35) / 100;
    var hues = new Array(N).fill(null);
    var current = -1;

    function applyTint(i) {
      var hs = hues[i];
      if (!hs || !tintEl) return;
      tintEl.style.setProperty('--fcx-tint-op', tintStrength.toFixed(2));
      tintEl.style.setProperty('--fcx-tint-col', 'hsla(' + Math.round(hs[0]) + ',80%,55%,0.75)');
    }

    items.forEach(function (item, i) {
      var img = item.querySelector('[data-fcx-sample]');
      if (!img) return;
      var run = function () {
        var hs = sampleImage(img);
        if (hs) {
          hues[i] = hs;
          var card = item.querySelector('.fcx-card');
          card.style.setProperty('--fcx-card-glow', 'hsla(' + Math.round(hs[0]) + ',75%,58%,0.32)');
          card.style.setProperty('--fcx-card-glow-strong', 'hsla(' + Math.round(hs[0]) + ',85%,62%,0.6)');
          if (i === Math.round(rendered)) applyTint(i);
        }
      };
      if (img.complete && img.naturalWidth) run();
      else img.addEventListener('load', run, { once: true });
    });

    /* movement state: toggles caption fade + suspends pulse while moving */
    var movingTimer = null;
    function markMoving() {
      if (stage && !stage.hasAttribute('data-moving')) stage.setAttribute('data-moving', '');
      var f = root.querySelector('.fcx-item.is-pulsing');
      if (f) f.classList.remove('is-pulsing');
      if (movingTimer) clearTimeout(movingTimer);
      movingTimer = setTimeout(markStopped, 160);
    }
    function markStopped() {
      if (stage) stage.removeAttribute('data-moving');
      var idx = clamp(Math.round(rendered), 0, N - 1);
      items.forEach(function (it, i) {
        if (i === idx) it.classList.add('is-pulsing'); else it.classList.remove('is-pulsing');
      });
    }

    /* ---- draw a given progress p (0..N-1) ---- */
    function draw(p) {
      for (var i = 0; i < N; i++) {
        var d = i - p;
        var e = clamp(1 - Math.abs(d), 0, 1);
        var ee = e * e * (3 - 2 * e);
        var it = items[i];
        it.style.setProperty('--d', d.toFixed(3));
        it.style.setProperty('--e', ee.toFixed(3));
      }
      var idx = clamp(Math.round(p), 0, N - 1);
      if (idx !== current) {
        current = idx;
        caps.forEach(function (cp, i) {
          if (i === idx) cp.setAttribute('data-active', ''); else cp.removeAttribute('data-active');
        });
        items.forEach(function (it, i) {
          if (i === idx) { it.setAttribute('data-focus', ''); void it.offsetWidth; }
          else it.removeAttribute('data-focus');
        });
        applyTint(idx);
        updateArrows(idx);
      }
    }

    function updateArrows(idx) {
      if (prevBtn) {
        if (idx <= 0) prevBtn.setAttribute('aria-disabled', 'true');
        else prevBtn.removeAttribute('aria-disabled');
      }
      if (nextBtn) {
        if (idx >= N - 1) nextBtn.setAttribute('aria-disabled', 'true');
        else nextBtn.removeAttribute('aria-disabled');
      }
    }

    /* ============================================================
       MOTION CORE
       Two motion models share one rAF loop:
         - arrow / keyboard steps: a fixed-duration eased tween, so every
           press glides at the same even pace all the way to the next card.
         - drag-follow and flick: a spring that chases `target` with EASE,
           for 1:1 finger tracking and momentum on release.
       ============================================================ */
    var rendered = 0, target = 0, velocity = 0, running = false;
    var EASE = 0.10;            // spring factor for drag-follow / flick settle
    var GLIDE_MS = 2000;        // duration of one arrow/keyboard glide
    var tween = null;           // active arrow glide { from, to, start, dur }, else null

    function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

    function maxP() { return N - 1; }

    function startLoop() { if (running) return; running = true; window.requestAnimationFrame(frame); }
    function frame() {
      /* arrow / keyboard: clock-driven eased tween, momentum ignored */
      if (tween && !dragging) {
        var tt = (performance.now() - tween.start) / tween.dur;
        if (tt >= 1) {
          rendered = target = tween.to;
          tween = null;
          draw(rendered);
          running = false;
          return;
        }
        rendered = tween.from + (tween.to - tween.from) * easeInOut(tt);
        markMoving();
        draw(rendered);
        window.requestAnimationFrame(frame);
        return;
      }
      /* drag-follow / flick: spring toward target with momentum */
      if (!dragging && Math.abs(velocity) > 0.0005) {
        target = clamp(target + velocity, 0, maxP());
        velocity *= 0.90;
        if (Math.abs(velocity) < 0.002) { velocity = 0; target = clamp(Math.round(target), 0, maxP()); }
      }
      var diff = target - rendered;
      rendered += diff * EASE;
      if (Math.abs(diff) > 0.0015) markMoving();
      if (Math.abs(diff) < 0.0008 && Math.abs(velocity) < 0.0006) {
        rendered = target;
        draw(rendered);
        running = false;
        return;
      }
      draw(rendered);
      window.requestAnimationFrame(frame);
    }

    function setTarget(p, immediateVel) {
      target = clamp(p, 0, maxP());
      if (typeof immediateVel === 'number') velocity = immediateVel;
      startLoop();
    }

    function step(dir) {
      var idx = clamp(Math.round(target) + dir, 0, maxP());
      if (idx === Math.round(target)) return;
      target = idx;                 // lock destination for arrow state + drag handoff
      velocity = 0;                 // drop any leftover flick momentum
      if (REDUCE) { rendered = idx; draw(rendered); return; }
      tween = { from: rendered, to: idx, start: performance.now(), dur: GLIDE_MS };
      markMoving();
      startLoop();
    }

    /* ---- ARROW BUTTONS ---- */
    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.preventDefault(); step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.preventDefault(); step(1); });

    /* ---- KEYBOARD (when section has focus) ---- */
    panel.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });

    /* ---- CLICK & DRAG ---- */
    var dragging = false;
    if (!REDUCE && panel) {
      var moved = false, startX = 0, startTarget = 0, lastX = 0, lastT = 0, vPx = 0, activePointer = null;
      var pxPerItem = function () { return Math.max(220, panel.clientWidth * 0.6); };

      panel.addEventListener('dragstart', function (e) { e.preventDefault(); });
      panel.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch' || e.button !== 0) return;
        // Presses on links, buttons or the caption are real clicks, never drags.
        if (e.target && e.target.closest && e.target.closest('a, button, .fcx-caption')) return;
        if (prevBtn && (e.target === prevBtn || prevBtn.contains(e.target))) return;
        if (nextBtn && (e.target === nextBtn || nextBtn.contains(e.target))) return;
        dragging = true; moved = false;
        tween = null;                      // hand control to the finger, cancel any arrow glide
        target = rendered;                 // freeze at the glide's current position, no self-motion while held
        startX = lastX = e.clientX; startTarget = target;
        lastT = performance.now(); vPx = 0; velocity = 0;
        activePointer = e.pointerId;
        panel.classList.add('is-grabbing');
        // NOTE: pointer capture is deferred to pointermove (first threshold
        // crossing) so plain clicks are not retargeted away from links.
        startLoop();
      });
      panel.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          try { panel.setPointerCapture(activePointer != null ? activePointer : e.pointerId); } catch (err) {}
        }
        if (moved) {
          e.preventDefault();
          markMoving();
          target = clamp(startTarget - dx / pxPerItem(), 0, maxP());
          var now = performance.now();
          var dt = Math.max(1, now - lastT);
          vPx = (e.clientX - lastX) / dt;
          lastX = e.clientX; lastT = now;
          startLoop();
        }
      });
      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        panel.classList.remove('is-grabbing');
        if (moved) {
          var perItemPx = pxPerItem();
          velocity = clamp((-vPx * 16) / perItemPx, -0.6, 0.6);
          if (Math.abs(velocity) < 0.01) { velocity = 0; setTarget(Math.round(target)); }
          else { startLoop(); }
          var swallow = function (ev) { ev.stopPropagation(); ev.preventDefault(); panel.removeEventListener('click', swallow, true); };
          panel.addEventListener('click', swallow, true);
          setTimeout(function () { panel.removeEventListener('click', swallow, true); }, 0);
        } else {
          setTarget(Math.round(rendered));   // grabbed without dragging: settle to the nearest card
        }
        try { if (e && e.pointerId != null) panel.releasePointerCapture(e.pointerId); } catch (err) {}
        activePointer = null;
      }
      panel.addEventListener('pointerup', endDrag);
      panel.addEventListener('pointercancel', endDrag);
    }

    /* ---- init ---- */
    if (REDUCE) {
      items.forEach(function (it) { it.style.setProperty('--e', '1'); it.style.setProperty('--d', '0'); });
      draw(0);
    } else {
      rendered = target = 0;
      draw(0);
      applyTint(0);
      markStopped();
      updateArrows(0);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-fcx]').forEach(initSection);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();

/* ───────────────────────────────────────────────────
   SCROLL REVEAL — the spotlight stage sits dark and empty until the section
   scrolls into the user's focus, then the first card makes its entrance and
   everything else follows. Pure additive layer: only toggles the fcx-armed /
   fcx-reveal classes consumed by featured-spotlight.css. — Claude
   ─────────────────────────────────────────────────── */
(function () {
  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCE || !('IntersectionObserver' in window)) return;

  function arm(root) {
    var panel = root.querySelector('[data-fcx-panel]');
    if (!panel || panel.__fcxRevealArmed) return;
    panel.__fcxRevealArmed = true;
    /* Only arm when the section starts below the fold; if it is already in
       view on load there is nothing to dramatise. */
    var r = panel.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.9) return;
    panel.classList.add('fcx-armed');
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.disconnect();
        panel.classList.remove('fcx-armed');
        panel.classList.add('fcx-reveal');
        /* replay the shine sweep on the focused card as it lands */
        var f = panel.querySelector('.fcx-item[data-focus]');
        if (f) { f.removeAttribute('data-focus'); void f.offsetWidth; f.setAttribute('data-focus', ''); }
        setTimeout(function () { panel.classList.remove('fcx-reveal'); }, 2600);
        break;
      }
    }, { threshold: 0.45 });
    io.observe(panel);
  }

  function armAll() { document.querySelectorAll('[data-fcx]').forEach(arm); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', armAll);
  else armAll();
  document.addEventListener('shopify:section:load', armAll);
})();
